import { useQuery } from "@tanstack/react-query";
import type { FilesSavedPayload, SavedFile } from "@codvero/shared";
import { Code, Download, Eye, FileArchive, FileText, FolderDown, X } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Markdown from "react-markdown";
import { useTranslation } from "react-i18next";
import remarkGfm from "remark-gfm";
import { filesApi } from "@/api/browser.ts";
import { cn } from "@/lib/cn.ts";
import { useImageViewer } from "@/ui/ImageViewer.tsx";
import { Alert, Spinner } from "@/ui/primitives.tsx";
import { useApiError } from "@/ui/useApiError.ts";

/** Files an agent links to by their place in the container: the workspace, and Antigravity's own artifacts folder. */
export const LOCAL_FILE = /^\/(workspace|home\/dev\/\.gemini\/antigravity-cli\/brain)\/./;
export const IMAGE_FILE = /\.(png|jpe?g|gif|webp|svg)$/i;

/**
 * A file from the conversation, opened over the page instead of in a new tab (where a file the platform only lets
 * you download flashed a blank tab and closed): pictures (SVG too, drawn as an image so its scripts never run),
 * Markdown rendered, a web page drawn without scripts or as code, any other text as code, the rest as a download.
 */
export function FilePreview({ projectId, path, onClose }: { projectId: string; path: string; onClose: () => void }) {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const viewImages = useImageViewer();
  const name = path.split("/").pop() ?? path;
  const image = IMAGE_FILE.test(path);
  const html = /\.html?$/i.test(path);
  const md = /\.(md|markdown)$/i.test(path);
  const pdf = /\.pdf$/i.test(path);
  const [asCode, setAsCode] = useState(false);
  const q = useQuery({ queryKey: ["files", projectId, "content", path], queryFn: () => filesApi.content(projectId, path), enabled: !image && !pdf });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  const raw = filesApi.rawUrl(projectId, path);
  const f = q.data;
  return createPortal(
    <div className="fixed inset-0 z-[110] grid place-items-center bg-black/60 p-3 pt-safe pb-safe" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label={name} className="flex h-full max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-1 border-b border-border px-3 py-2">
          <span dir="ltr" className="min-w-0 flex-1 truncate font-mono text-xs" title={path}>{name}</span>
          {html && (
            <button type="button" onClick={() => setAsCode(!asCode)} className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-fg-muted hover:bg-surface-2 hover:text-fg">
              {asCode ? <><Eye className="size-3.5" /> {t("files.preview")}</> : <><Code className="size-3.5" /> {t("files.code")}</>}
            </button>
          )}
          <a href={filesApi.rawUrl(projectId, path, true)} download={name} className="rounded-md p-1.5 text-fg-muted hover:bg-surface-2 hover:text-fg" aria-label={t("files.download")} title={t("files.download")}><Download className="size-4" /></a>
          <button type="button" onClick={onClose} className="rounded-md p-1.5 text-fg-muted hover:bg-surface-2 hover:text-fg" aria-label={t("common.close")}><X className="size-4" /></button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto bg-bg">
          {image && (
            <button type="button" className="grid h-full w-full cursor-zoom-in place-items-center p-4" onClick={() => viewImages([{ src: raw, name, downloadUrl: filesApi.rawUrl(projectId, path, true) }])}>
              <img src={raw} alt={name} className={cn("max-h-full max-w-full rounded-md bg-[repeating-conic-gradient(#8882_0_25%,transparent_0_50%)] bg-[length:16px_16px]", /\.svg$/i.test(path) && "w-[min(100%,36rem)]")} />
            </button>
          )}
          {pdf && <iframe title={name} src={raw} className="h-full min-h-[70vh] w-full bg-white" />}
          {!image && q.isLoading && <div className="grid place-items-center p-6"><Spinner /></div>}
          {!image && q.error && <div className="p-4"><Alert tone="danger">{toMsg(q.error)}</Alert></div>}
          {f && f.binary && (
            <div className="grid place-items-center gap-3 p-8 text-center text-sm text-fg-muted">
              <FileArchive className="size-10" />
              <p>{t("files.binary")}</p>
              <button type="button" className="flex items-center gap-1.5 rounded-md bg-accent px-3 py-2 text-sm font-medium text-white" onClick={() => void saveToDevice(filesApi.rawUrl(projectId, path, true), name, f.mime)}><Download className="size-4" /> {t("chat.savedFiles.save")}</button>
            </div>
          )}
          {f && !f.binary && html && !asCode && (
            // drawn with scripts, forms, pop-ups and top-level navigation disabled (no allow-scripts: nothing in it
            // can run, so its same-origin flag only lets the browser draw it; an opaque srcdoc frame stayed blank)
            <iframe title={name} sandbox="allow-same-origin" srcDoc={f.content ?? ""} className="h-full min-h-[60vh] w-full bg-white" />
          )}
          {f && !f.binary && md && (
            <div dir="auto" className="prose-chat max-w-none p-4 text-sm leading-7 [&_a]:text-accent [&_a]:underline [&_h1]:text-lg [&_h1]:font-semibold [&_h2]:font-semibold [&_li]:ms-4 [&_ol]:list-decimal [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-surface-2 [&_pre]:p-3 [&_ul]:list-disc">
              <Markdown remarkPlugins={[remarkGfm]}>{f.content ?? ""}</Markdown>
            </div>
          )}
          {f && !f.binary && !md && (!html || asCode) && (
            <pre dir="ltr" className={cn("whitespace-pre-wrap break-words p-4 font-mono text-[12px] leading-5")}>{f.content}{f.truncated && `\n…[${t("files.truncated")}]`}</pre>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

const fmtSize = (n: number) => (n < 1024 ? `${n} B` : n < 1048576 ? `${Math.round(n / 1024)} KB` : `${(n / 1048576).toFixed(1)} MB`);

/**
 * Saves a kept file on the device: the share sheet where the browser has one for files (an iPad or iPhone, where a
 * Home Screen app cannot download), otherwise a normal download.
 */
export async function saveToDevice(url: string, name: string, mime: string) {
  try {
    const nav = navigator as Navigator & { canShare?: (d: { files: File[] }) => boolean };
    if (nav.canShare && nav.share) {
      const blob = await (await fetch(url, { credentials: "same-origin" })).blob();
      const file = new File([blob], name, { type: mime || blob.type });
      if (nav.canShare({ files: [file] })) return await nav.share({ files: [file] });
    }
  } catch (e) {
    if ((e as { name?: string }).name === "AbortError") return; // the person closed the share sheet
  }
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
}

/** The files a tool brought back, kept by the platform in the space (or the conversation's folder) and shown from there. */
export function SavedFiles({ projectId, payload }: { projectId: string; payload: FilesSavedPayload }) {
  const { t } = useTranslation();
  const viewImages = useImageViewer();
  const [open, setOpen] = useState<string | null>(null);
  const images = payload.files.filter((f) => f.mime.startsWith("image/"));
  const videos = payload.files.filter((f) => f.mime.startsWith("video/") || f.mime.startsWith("audio/"));
  const others = payload.files.filter((f) => !images.includes(f) && !videos.includes(f));
  const raw = (f: SavedFile, download = false) => filesApi.rawUrl(projectId, f.path, download);
  return (
    <div className="rounded-lg border border-border bg-surface/40 text-xs">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2 text-fg-muted">
        <FolderDown className="size-3.5 shrink-0 text-success" />
        <span className="min-w-0 flex-1 truncate">{payload.inSpace ? t("chat.savedFiles.space", { count: payload.files.length }) : t("chat.savedFiles.folder", { count: payload.files.length })}</span>
      </div>
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2 p-2">
          {images.map((f, i) => (
            <button key={f.path} type="button" className="block cursor-zoom-in" title={f.name} onClick={() => viewImages(images.map((x) => ({ src: raw(x), name: x.name, downloadUrl: raw(x, true) })), i)}>
              <img src={raw(f)} alt={f.name} loading="lazy" className={cn("max-h-40 max-w-[min(100%,14rem)] rounded border border-border", /\.svg$/i.test(f.name) && "w-40 bg-white")} />
            </button>
          ))}
        </div>
      )}
      {videos.map((f) => (
        <div key={f.path} className="px-2 pb-2">
          {f.mime.startsWith("audio/") ? <audio controls preload="metadata" src={raw(f)} className="w-full max-w-md" /> : <video controls playsInline preload="metadata" src={raw(f)} className="max-h-80 max-w-full rounded-md border border-border bg-black" />}
        </div>
      ))}
      {[...videos, ...others].length > 0 && (
        <ul className="divide-y divide-border border-t border-border">
          {[...others, ...videos].map((f) => (
            <li key={f.path} className="flex items-center gap-2 px-3 py-2">
              {/\.(zip|7z|rar|tar|gz)$/i.test(f.name) ? <FileArchive className="size-4 shrink-0 text-fg-muted" /> : <FileText className="size-4 shrink-0 text-fg-muted" />}
              <button type="button" dir="auto" className="min-w-0 flex-1 truncate text-start hover:underline" onClick={() => setOpen(f.path)}>{f.name}</button>
              <span className="shrink-0 text-fg-muted">{fmtSize(f.size)}</span>
              <button type="button" className="flex shrink-0 items-center gap-1 rounded-md border border-border px-2 py-1 hover:border-accent/60" onClick={() => void saveToDevice(raw(f, true), f.name, f.mime)}><Download className="size-3.5" /> {t("chat.savedFiles.save")}</button>
            </li>
          ))}
        </ul>
      )}
      {open && <FilePreview projectId={projectId} path={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
